import { queryOptions, useMutation, useQuery } from '@tanstack/react-query';

import { queryClient } from '~app/query-client';

import { baseAPI } from './base-api';

interface CurrencyAPIPrams {
  name: string;
}

interface Currency {
  _id: string;
  name: string;
  symbol?: string;
}

// Currency symbols mapping
const currencySymbols: Record<string, string> = {
  USD: '$',
  CAD: 'C$',
  AUD: 'A$',
  NZD: 'NZ$',
  GBP: '£',
};

// Fetch currency API
export const fetchCurrencyAPI = async () => {
  const resp = await baseAPI.get<Currency>(`/currency`);
  const data = resp.data;

  // Add symbol to the response
  if (data.name && currencySymbols[data.name]) {
    data.symbol = currencySymbols[data.name];
  }

  return data;
};

// Create currency API
export const createCurrencyAPI = async (params: CurrencyAPIPrams) => {
  const resp = await baseAPI.post<Currency>('/currency', params);
  const data = resp.data;

  // Add symbol to the response
  if (data.name && currencySymbols[data.name]) {
    data.symbol = currencySymbols[data.name];
  }

  return data;
};

// Currency query options
export const getCurrencyOptions = () => {
  return queryOptions({
    queryKey: ['currency'],
    queryFn: () => fetchCurrencyAPI(),
    staleTime: 5 * 60 * 1000, // Data remains fresh for 5 minutes
  });
};

// Get currency hook
export const useGetCurrency = () => {
  return useQuery(getCurrencyOptions());
};

// Create currency mutation hook
export const useCreateCurrencyMutation = () => {
  return useMutation({
    mutationFn: async (params: CurrencyAPIPrams) => {
      const response = await createCurrencyAPI(params);
      return response;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['currency'],
      });
    },
  });
};
